import type { SpecSelectionRung } from '../sku/select-spec-row.js';

/**
 * derive-input.ts — SIL-188 / SIL-197
 *
 * Pure derivation of a BoaviztAPI input payload from a SKU's active OEM
 * spec + class_attributes. No I/O — fully unit-testable.
 *
 * Field mapping (what we trust vs what we leave to archetype defaults):
 *
 *   cpu.units       sku_oem_spec.cpu_socket_count ?? cpu_max_sockets,
 *                   else class_attributes.cpu_sockets, else archetype default
 *   cpu.name        resolved by the compute CLI (SIL-197): the deriver emits
 *                   a free-text cpuNameCandidate (sku_oem_spec.cpu_model or
 *                   the scraped processor-family text); the CLI resolves it
 *                   against BoaviztAPI's CPU vocabulary with a cross-family
 *                   guard and only a successful resolution sets cpu.name.
 *   cpu.core_units  sku_oem_spec.cpu_cores
 *   ram             DERIVED (SIL-395) from max_ram_gb, which is a MAX
 *                   SUPPORTED ceiling, not an installed configuration.
 *                   Feeding max_ram_gb straight through would overstate
 *                   embodied carbon by an order of magnitude, so we instead
 *                   assume a realistic fraction is actually installed (see
 *                   `deriveRamConfiguration` / RAM_* constants below) and
 *                   record exactly how the figure was reached in
 *                   defaulted_fields['ram'] — estimated with scraped DIMM
 *                   slots, estimated with an assumed slot count, or the
 *                   BoaviztAPI archetype default when no max_ram_gb signal
 *                   exists at all. NEVER presented as a measured value.
 *                   EXCEPTION (SIL-413): soldered-memory CPUs (NVIDIA Grace)
 *                   have no fraction to estimate — max_ram_gb IS the
 *                   installed figure, taken exactly, with no DIMM count to
 *                   derive. See `deriveSolderedRamConfiguration` /
 *                   `isSolderedMemoryCpu`.
 *   disk            archetype-defaulted (no per-SKU storage config data).
 *   power_supply    units from class_attributes.psu_redundancy
 *                   (N → 1, N+1 / 2N → 2); else archetype default.
 *   case            type 'rack' when rack_units / form factor indicate a
 *                   rack chassis, 'blade' when the OEM form factor says
 *                   blade; else archetype default.
 *
 * GPU composition (documented contract for gpu-bearing servers):
 *   BoaviztAPI's /v1/server/ model has no GPU component, so a GPU server is
 *   computed as   server_impacts + gpu_units × gpu_component_impacts
 *   with value/min/max each summed independently. gpu_units comes from
 *   class_attributes.gpu_slots ?? sku_oem_spec.max_gpu_count ?? an explicit
 *   count in the SKU's own name ("8x B200", "8XB200", "8-GPU") ?? the
 *   curated GPU_PLATFORM_TABLE (fixed-baseboard platforms such as the Dell
 *   PowerEdge XE9680 = 8) ?? an assumed 1. SIL-2178: before that ladder, a
 *   server whose name said "8x" but had no slot column became ONE GPU, and
 *   an XE9680 row with no GPU model in its name lost its GPUs entirely. The
 *   GPU model name comes from sku_oem_spec.gpu_model or is parsed out of the
 *   SKU's display/model name (e.g. "POWEREDGE R760XA NVIDIA H100 PCIE"),
 *   else the platform table's documented default model. Which rung fired is
 *   `accelerator.unitsSource`; every rung below max_gpu_count is also
 *   disclosed in defaultedFields['gpu.units'] (and a platform-default model
 *   in defaultedFields['gpu.model']).
 *   This is the EMBODIED pipeline's contract (compute-carbon-footprints.ts) —
 *   unchanged by SIL-383/388/389 below.
 *
 * Accelerator USE-PHASE power (SIL-383/388/389 — the use-phase pipeline
 * only; embodied is untouched):
 *   `accelerator` on a `gpu_server` derivation carries two INDEPENDENT,
 *   separately-declared assumptions that the use-phase pipeline
 *   (carbon-use-phase.ts) folds into `configuration.gpu.usage.avg_power` on
 *   the archetype-default power path ONLY — measured whole-system watts
 *   already include GPU draw, so applying this a second time would
 *   double-count (SIL-389's hard constraint):
 *     - `unitsSource` (SIL-388) — accelerator COUNT is slot CAPACITY
 *       (class_attributes.gpu_slots ?? sku_oem_spec.max_gpu_count), not a
 *       populated count. Disclosed as an upper bound, never silently treated
 *       as "installed."
 *     - `tdp` (SIL-389) — per-accelerator TDP × a 0.50 workload derate,
 *       citing BoaviztAPI's own archetype CPU workload assumption
 *       (`time_workload = 50%`) rather than inventing a constant. `null`
 *       when the resolved GPU name/token has no entry in
 *       `ACCELERATOR_TDP_TABLE` — the model is real but its draw stays
 *       omitted (disclosed via `defaultedFields['gpu.tdp']`), same
 *       understatement shape as before this ticket, just narrower in scope.
 */

/**
 * rack_system composition (SIL-638): a rack-scale SKU (GB200/GB300 NVL72,
 * Dell XE8712/8812/9712, etc.) is NOT a single chassis with N GPU slots —
 * it is `node_count` INDEPENDENT compute trays, each with its own
 * CPU/RAM/mainboard/enclosure, sharing a rack-wide GPU pool. Naively
 * reusing gpu_server's "1 server + N gpu" composition would price ONE
 * tray's chassis material against the WHOLE rack's GPU count, undercounting
 * every other tray's mainboard/enclosure/PSU material. This kind instead
 * composes:
 *
 *   embodied = node_count x server_impacts(one tray) + gpu_units x gpu_component_impacts
 *
 * driven by new structured `sku.class_attributes` fields (no migration —
 * jsonb column): `node_count`, `gpus_per_node` (or a direct `total_gpu_count`
 * when the SKU is more naturally specified that way), `cpu_model`,
 * `cpu_count_per_node`, `ram_per_node_gb`. `ram_per_node_gb` is taken
 * EXACTLY (no RAM_FILL_RATIO estimate, same reasoning as the soldered-memory
 * path above) — it is meant to be sourced as the tray's actual shipped
 * configuration, not a max-supported ceiling to fraction down.
 *
 * `deriveRackSystemConfig` returns `{ kind: 'unsupported' }` when ANY
 * required field is missing from class_attributes — deliberately: a
 * partially-specified rack composition (e.g. node_count with no GPU count)
 * is worse than no figure at all, since BoaviztAPI would otherwise silently
 * archetype-default the missing piece into a number that looks computed.
 *
 * KNOWN LIMITATION, even with every field correctly sourced: BoaviztAPI only
 * models server-chassis + GPU components. An NVL72-class rack also has
 * NVSwitch/NVLink switch trays, the rack frame, liquid-cooling
 * manifolds/CDUs, and cabling — none of which map to "N x server + M x gpu".
 * A manufacturer's own whole-rack PCF would include these; this composition
 * systematically will not, so every successful `rack_system` derivation
 * carries a `defaultedFields['rack.scope']` disclosure to that effect (never
 * omitted, never phrased as a complete figure).
 */

/** Row shape the deriver needs (subset of sku ⋈ sku_oem_spec). */
export interface CarbonSourceRow {
  skuId: string;
  verifiedSku: string;
  modelName: string;
  displayName: string | null;
  assetClassId: string;
  classAttributes: Record<string, unknown> | null;
  // active OEM spec fields (all nullable — spec coverage is sparse)
  cpuModel: string | null;
  cpuSocketCount: number | null;
  cpuMaxSockets: number | null;
  cpuCores: number | null;
  maxRamGb: number | null;
  /**
   * SIL-393/395: DIMM slot count scraped from OEM sources. Caps the module
   * count `deriveRamConfiguration` derives from max_ram_gb — without it a
   * 6144 GB ceiling yields 96 modules, which no chassis holds. Expect NULL
   * everywhere until the dimm_slots scrape backfill runs; the deriver falls
   * back to `RAM_ASSUMED_DIMM_SLOTS` (32) in that case.
   */
  dimmSlots: number | null;
  rackUnits: number | null;
  formFactor: string | null;
  powerSupplyWatts: number | null;
  gpuModel: string | null;
  maxGpuCount: number | null;
  vramGb: string | number | null;
  /**
   * SIL-197: free-text CPU family/support description from the SKU's OEM
   * spec raw payloads (e.g. "4th and 5th Gen Intel Xeon Scalable",
   * "AMD EPYC 9004 Series (Genoa)"). Used as the cpu.name resolution
   * candidate when cpu_model is absent.
   */
  cpuFamilyText: string | null;
  /**
   * SIL-402: which `sku_oem_spec` row every field above was read from, and
   * which rung of the selection rule fired (pinned / latest-with-max_ram_gb
   * / latest / none). See carbon/select-spec-row.ts — NEVER a per-column
   * mix across rows. Folded into the compute pipelines' input_payload as
   * provenance (not defaultedFields — a pinned or latest selection is a
   * successful selection, not a missing-data default).
   */
  specRowId: string | null;
  specSelectionRung: SpecSelectionRung;
}

export interface ServerConfigurationPayload {
  model: { type: string };
  configuration: {
    cpu?: {
      units?: number;
      name?: string;
      core_units?: number;
      family?: string;
      tdp?: number;
    };
    power_supply?: { units: number };
    /**
     * SIL-395: derived installed RAM configuration — a single module tier
     * (`units` × `capacity` GB each). See `deriveRamConfiguration` for the
     * derivation and its named constants. Present whenever max_ram_gb is
     * known; absent (archetype-defaulted) only when the SKU carries no RAM
     * signal at all.
     */
    ram?: Array<{ units: number; capacity: number }>;
    /**
     * SIL-383/389 — use-phase-only addition. Verified against the pinned
     * BoaviztAPI 2.3.0 `/openapi.json` (`ConfigurationServer.gpu` ->
     * `#/components/schemas/GPU`, `GPU.usage` -> `#/components/schemas/
     * Usage`, same `avg_power` field as the top-level `usage` block) AND
     * empirically: a live POST /v1/server/ with this shape folds the GPU's
     * `usage.avg_power` into the response's `impacts.gwp.use` alongside the
     * chassis archetype, in one call — matching PR #528's "GPU power is
     * added to the modelled server power" (SIL-389). Only set by
     * carbon-use-phase.ts, only on the archetype-default power path (never
     * by the embodied CLI, which still sums a SEPARATE `/v1/component/gpu`
     * call — see the module-header note).
     */
    gpu?: { units: number; name: string; usage: { avg_power: number } };
  };
  /**
   * Use-phase-only addition (SIL-343): the embodied CLI never sets this
   * (embodied calls omit `duration`/`usage` entirely so BoaviztAPI returns
   * whole-lifecycle embedded impacts). The use-phase CLI reuses this SAME
   * `ServerConfigurationPayload.configuration` shape for hardware identity
   * but layers its own `usage` block (avg_power / usage_location /
   * use_time_ratio) on top before calling `computeServer`.
   */
  usage?: Record<string, unknown>;
}

export type CarbonDerivation =
  | {
      kind: 'server';
      payload: { server: ServerConfigurationPayload };
      /** Free-text CPU identity to resolve via BoaviztAPI (SIL-197); null = no signal. */
      cpuNameCandidate: string | null;
      defaultedFields: Record<string, string>;
      /** SIL-396: which rung of the RAM precedence ladder fired for this row. */
      ramProvenanceKind: RamProvenanceKind;
    }
  | {
      kind: 'gpu_server';
      payload: { server: ServerConfigurationPayload; gpu: { name: string; units: number } };
      cpuNameCandidate: string | null;
      defaultedFields: Record<string, string>;
      /** SIL-388/389 — see the module-header "Accelerator USE-PHASE power"
       * note. Consumed by carbon-use-phase.ts only. */
      accelerator: AcceleratorPowerDerivation;
      /** SIL-396: which rung of the RAM precedence ladder fired for this row. */
      ramProvenanceKind: RamProvenanceKind;
    }
  | {
      kind: 'gpu';
      payload: { gpu: { name: string; units: number } };
      defaultedFields: Record<string, string>;
    }
  | {
      /**
       * SIL-638: multi-node rack-scale system (e.g. GB200/GB300 NVL72, Dell
       * XE8712/8812/9712) — 18 (or however many) independent compute trays,
       * each with its own chassis/CPU/RAM, sharing a common GPU pool count.
       * See the module-header "rack_system composition" note below for why
       * this is a distinct node_count x server + gpu_units x gpu composition
       * and NOT a reuse of gpu_server's single-chassis "1 server + N gpu".
       */
      kind: 'rack_system';
      payload: {
        /** ONE compute tray's configuration — multiplied by nodeCount below,
         * never the whole-rack chassis. */
        server: ServerConfigurationPayload;
        nodeCount: number;
        /** Total GPU units across the WHOLE rack (nodeCount x gpus-per-node,
         * or a directly-supplied rack-wide total — see deriveRackSystemConfig). */
        gpu: { name: string; units: number };
      };
      cpuNameCandidate: string | null;
      defaultedFields: Record<string, string>;
    }
  | {
      kind: 'cpu_component';
      payload: { cpu: CpuComponentPayload };
      /** Free-text CPU identity to resolve via BoaviztAPI (SIL-197), same
       * guarded resolution as the server path. Null = no signal (unsupported). */
      cpuNameCandidate: string;
      defaultedFields: Record<string, string>;
    }
  | {
      kind: 'memory_component';
      payload: { ram: { units: number; capacity: number } };
      defaultedFields: Record<string, string>;
    }
  | { kind: 'unsupported'; reason: string };

/**
 * SIL-433: BoaviztAPI /v1/component/cpu request body for a STANDALONE cpu
 * SKU. `units: 1` — one physical processor, not a multi-socket board (that
 * is the server path's concern, unchanged). `name`/`family`/`tdp` are filled
 * by the compute CLI after the SAME guarded name_to_cpu resolution the
 * server path uses (resolveCpuArchetype) — never invented here.
 */
export interface CpuComponentPayload {
  units: number;
  core_units?: number;
  name?: string;
  family?: string;
  tdp?: number;
}

// ── SIL-388/389: accelerator use-phase power ─────────────────────────────────

/** Accelerator population count source (SIL-388) — always CAPACITY, never a
 * populated/installed count; the derived tier/copy must disclose this. */
export type AcceleratorUnitsSource =
  | 'gpu_slots'
  | 'max_gpu_count'
  /** SIL-2178: an explicit count in the SKU's own model/display/verified name. */
  | 'name_count'
  /** SIL-2178: GPU_PLATFORM_TABLE (a fixed-baseboard platform's documented GPU count). */
  | 'platform_table'
  | 'assumed_one';

/** Every AcceleratorUnitsSource, in precedence order (zod enums reuse this). */
export const ACCELERATOR_UNITS_SOURCES = [
  'gpu_slots',
  'max_gpu_count',
  'name_count',
  'platform_table',
  'assumed_one',
] as const satisfies ReadonlyArray<AcceleratorUnitsSource>;

/** Human-readable origin of an accelerator count, for provenance notes. */
export function describeAcceleratorUnitsSource(source: AcceleratorUnitsSource): string {
  switch (source) {
    case 'gpu_slots':
      return 'class_attributes.gpu_slots';
    case 'max_gpu_count':
      return 'sku_oem_spec.max_gpu_count';
    case 'name_count':
      return 'an explicit GPU count in the SKU name';
    case 'platform_table':
      return 'the GPU platform table (documented fixed-baseboard GPU count)';
    case 'assumed_one':
      return 'no count signal (assumed 1)';
  }
}

/**
 * SIL-389's 50% workload derate — cited to BoaviztAPI's own archetype CPU
 * workload assumption (`time_workload = 50%`, visible in every
 * `raw_response.verbose` block as `{"value": 50, "status": "ARCHETYPE"}`),
 * NOT calibrated from our own measured fleet (rejected — see SIL-389: every
 * measured GPU-slot asset belongs to a synthetic-telemetry test org). Kept
 * as its own named constant, separate from `ACCELERATOR_TDP_TABLE`, so the
 * derate can be revised on different evidence than any one model's TDP —
 * the two are independent declared terms per SIL-388/389, never collapsed.
 */
export const USE_PHASE_GPU_WORKLOAD_DERATE_FACTOR = 0.5;

/**
 * Per-accelerator TDP table (SIL-389). BoaviztAPI 2.3.0's
 * `gpu_specs.csv` carries no power column at all (verified against
 * upstream `main` — see SIL-389's ticket body), so there is no archetype
 * power to defer to for any of these models; PR #528 makes supplying
 * `gpu.usage.avg_power` the upstream-sanctioned path instead of inventing a
 * power model from incomplete data.
 *
 * Stores TDP only — never a pre-derated avg_power — so the 0.50 workload
 * factor stays a single, separately-tunable knob
 * (`USE_PHASE_GPU_WORKLOAD_DERATE_FACTOR`) rather than baked per row.
 *
 * Order matters: first match wins. A100 variant patterns (SXM4/PCIe) MUST
 * precede the bare `A100` fallback, and `L40S` MUST precede `L4` — same
 * discipline as `GPU_TOKEN_RE`.
 */
const ACCELERATOR_TDP_TABLE: ReadonlyArray<{ pattern: RegExp; name: string; tdpWatts: number }> = [
  { pattern: /A100.*SXM/i, name: 'NVIDIA A100 SXM4', tdpWatts: 400 },
  { pattern: /A100.*PCI/i, name: 'NVIDIA A100 PCIe', tdpWatts: 300 },
  // Bare "A100" with no PCIe/SXM4 qualifier in the resolved name (e.g. the
  // name-parsing fallback only ever extracts the short family token, never
  // a form-factor suffix) — default to PCIe, the lower-draw variant, as the
  // conservative choice between the two.
  { pattern: /\bA100\b/i, name: 'NVIDIA A100 PCIe', tdpWatts: 300 },
  { pattern: /\bL40S\b/i, name: 'NVIDIA L40S', tdpWatts: 350 },
  { pattern: /\bL4\b/i, name: 'NVIDIA L4', tdpWatts: 72 },
  { pattern: /\bA30\b/i, name: 'NVIDIA A30', tdpWatts: 165 },
  { pattern: /\bA10\b/i, name: 'NVIDIA A10', tdpWatts: 150 },
  { pattern: /MI210/i, name: 'AMD Instinct MI210', tdpWatts: 300 },
  { pattern: /MI300X/i, name: 'AMD Instinct MI300X', tdpWatts: 750 },
  { pattern: /H200/i, name: 'NVIDIA H200', tdpWatts: 600 },
];

export interface AcceleratorTdpMatch {
  /** Canonical display name (may differ from the input text) — for provenance/logs. */
  name: string;
  tdpWatts: number;
}

/**
 * Look up a resolved GPU name/token against `ACCELERATOR_TDP_TABLE`. Returns
 * `null` when the model has no TDP entry — e.g. H100, V100, MI250X are all
 * real accelerators in the catalog but are NOT in SIL-389's decided table;
 * that omission is deliberate (decision scope), not a bug — such assets keep
 * their draw omitted rather than guessing a TDP.
 *
 * @pure
 */
export function resolveAcceleratorTdp(gpuName: string): AcceleratorTdpMatch | null {
  for (const entry of ACCELERATOR_TDP_TABLE) {
    if (entry.pattern.test(gpuName)) return { name: entry.name, tdpWatts: entry.tdpWatts };
  }
  return null;
}

export interface AcceleratorPowerDerivation {
  unitsSource: AcceleratorUnitsSource;
  /** `null` when `resolveAcceleratorTdp` found no table entry for the
   * resolved GPU name — accelerator draw stays omitted for this asset. */
  tdp: {
    name: string;
    tdpWatts: number;
    derateFactor: number;
    /** `tdpWatts * derateFactor` — PER ACCELERATOR, before multiplying by units. */
    avgPowerWatts: number;
  } | null;
}

/**
 * GPU family tokens we recognize inside free-text model names. Order
 * matters: longer/more-specific tokens first so "L40S" never matches "L4".
 */
const GPU_TOKEN_RE =
  /\b(GH200|H200|H100|H800|A100|A800|A30|A40|A16|A10G?|L40S|L40|L4|V100S?|P100|P40|T4|B300|B200|B100|RTX\s?A?\d{4}|MI\d{2,3}X?|GAUDI\s?\d)\b/i;

/**
 * SIL-2178: split a glued count prefix ("8XB200", "8XMI300X") into
 * "8X B200" so the word-bounded token regex can see the GPU token. Only a
 * word-initial 1-2 digit number directly followed by X and a letter is
 * split, so model codes that merely contain an X ("XE9680", "R760XA") are
 * left alone.
 */
function splitGluedCount(upper: string): string {
  return upper.replace(/\b(\d{1,2})X(?=[A-Z])/g, '$1X ');
}

/**
 * SIL-2178: Cisco UCS B-series blade model codes ("UCSB-B200-M6",
 * "UCS B200 M5", "UCSB_B200_M3") carry a B200 that is a blade, not an NVIDIA
 * Blackwell GPU. Two guards:
 *   - "B<nnn>" followed by a Cisco-style "M<gen>" (space, hyphen or
 *     underscore separated) is removed; NVIDIA never suffixes a GPU that way.
 *   - in any UCS context, every bare "B<nnn>" is removed, so a blade name
 *     with no generation suffix still never reads as a GPU. Real GPU tokens
 *     in a UCS name (a C-series with "2x T4") are kept.
 * Lookarounds instead of \b, because "_" is a word character
 * ("UCSB-B200-M6_2X6330CPU").
 */
function stripNonGpuModelCodes(upper: string): string {
  let out = upper.replace(/(?<![A-Z0-9])B\d{3}[\s_-]*M\d{1,2}(?!\d)/g, ' ');
  if (/UCS/.test(out)) out = out.replace(/(?<![A-Z0-9])B\d{3}(?!\d)/g, ' ');
  return out;
}

/** Extract the GPU family token (e.g. "H100") from a free-text name. */
export function extractGpuToken(name: string | null | undefined): string | null {
  if (!name) return null;
  const m = GPU_TOKEN_RE.exec(stripNonGpuModelCodes(splitGluedCount(name.toUpperCase())));
  return m ? m[1]!.replace(/\s+/g, ' ').trim() : null;
}

/** Same bound as the 'server' class_attributes schema's gpu_slots (0-16). */
const MAX_NAMED_GPU_COUNT = 16;

/**
 * SIL-2178: an explicit accelerator count stated in a free-text name:
 * "8x NVIDIA HGX B200", "8XB200", "8 x H100", "HGX H100 8-GPU", "4 GPU".
 * The "N x" form must be followed (within three words) by a recognized GPU
 * token, so "2x Xeon" or "8x 800Gb/s" never count. Returns null when no
 * count is stated or the count is outside 1..16.
 */
export function extractGpuCountFromName(name: string | null | undefined): number | null {
  if (!name) return null;
  const upper = splitGluedCount(name.toUpperCase().replace(/_/g, ' '));
  const times = /\b(\d{1,2})\s*[X×]\s+((?:[A-Z][A-Z-]*\s+){0,3}\S+)/g;
  for (let m = times.exec(upper); m; m = times.exec(upper)) {
    const n = Number(m[1]);
    if (n >= 1 && n <= MAX_NAMED_GPU_COUNT && extractGpuToken(m[2])) return n;
    // A miss must not swallow the next count: "2X INTEL XEON 4X T4" would
    // otherwise consume "4X" as one of the words after "2X".
    times.lastIndex = m.index + 1;
  }
  const perGpu = /\b(\d{1,2})\s*-?\s*GPUS?\b/.exec(upper);
  if (perGpu) {
    const n = Number(perGpu[1]);
    if (n >= 1 && n <= MAX_NAMED_GPU_COUNT) return n;
  }
  return null;
}

/**
 * SIL-2178: GPU platforms whose GPU complement is a fixed baseboard (HGX /
 * OAM / SXM), so the platform model alone fixes the accelerator count even
 * when the SKU row carries no gpu_slots, no OEM spec row, and no GPU model in
 * its name (prod's DELL_POWEREDGE_XE9680 row had none of the three, so it was
 * priced as a bare 900 kg chassis). Only a count the OEM documents as fixed
 * belongs here; a PCIe platform with "up to N" optional GPUs does not.
 * `defaultGpuName` is used only when no GPU model resolves from the row
 * itself. For the XE9680 it is the platform's first validated configuration
 * (440_seed_oem_catalog M0001); it is disclosed in defaultedFields
 * ['gpu.model']. BoaviztAPI 2.3.0 prices H100/H200/B200 SXM alike (575 kg),
 * so the default model does not move the embodied figure.
 */
const GPU_PLATFORM_TABLE: ReadonlyArray<{
  pattern: RegExp;
  platform: string;
  gpuUnits: number;
  defaultGpuName: string;
  source: string;
}> = [
  {
    // The trailing \b excludes XE9680L (its own entry below).
    pattern: /\bXE9680\b/,
    platform: 'Dell PowerEdge XE9680',
    gpuUnits: 8,
    defaultGpuName: 'NVIDIA H100 SXM5',
    source: 'Dell PowerEdge XE9680 technical guide: 8x SXM5 / OAM accelerators on one baseboard',
  },
  {
    pattern: /\bXE9680L\b/,
    platform: 'Dell PowerEdge XE9680L',
    gpuUnits: 8,
    defaultGpuName: 'NVIDIA H200 SXM5',
    source: 'Dell PowerEdge XE9680L technical guide: 8x SXM accelerators on one baseboard',
  },
  {
    pattern: /\bXE8640\b/,
    platform: 'Dell PowerEdge XE8640',
    gpuUnits: 4,
    defaultGpuName: 'NVIDIA H100 SXM5',
    source: 'Dell PowerEdge XE8640 technical guide: 4x NVIDIA H100 SXM5 (HGX H100 4-GPU)',
  },
  {
    pattern: /\bXE9640\b/,
    platform: 'Dell PowerEdge XE9640',
    gpuUnits: 4,
    defaultGpuName: 'NVIDIA H100 SXM5',
    source: 'Dell PowerEdge XE9640 technical guide: 4x SXM5 / OAM accelerators',
  },
  {
    // Every x86 DGX (A100/H100/H200/B200/B300) is an 8-GPU system. The GPU
    // model is always in the name, so defaultGpuName is a last resort.
    pattern: /\bDGX\s+(A100|H100|H200|B200|B300)\b/,
    platform: 'NVIDIA DGX (8-GPU)',
    gpuUnits: 8,
    defaultGpuName: 'NVIDIA H100 SXM5',
    source: 'NVIDIA DGX A100/H100/H200/B200/B300 datasheets: 8 GPUs per system',
  },
  {
    pattern: /\bSR680A\s+V3\b/,
    platform: 'Lenovo ThinkSystem SR680a V3',
    gpuUnits: 8,
    defaultGpuName: 'NVIDIA H200 SXM5',
    source: 'Lenovo ThinkSystem SR680a V3 product guide: 8x NVIDIA HGX SXM GPUs (migration 992: "8-GPU AI server")',
  },
  {
    pattern: /\bG893\b/,
    platform: 'Gigabyte G893',
    gpuUnits: 8,
    defaultGpuName: 'NVIDIA H200 SXM5',
    source: 'Gigabyte G893 datasheet: 8U 8-GPU HGX / OAM server (migration 961)',
  },
  {
    // Last, as the most generic rule: Blackwell B100/B200/B300 data-center
    // GPUs have no PCIe card; the x86 server form factor is the 8-GPU HGX
    // baseboard. Denser Grace sleds (Gigabyte XB13 "2XB200") state their
    // count in gpu_slots or the name, which both take precedence over this
    // table. GB200/GB300 do not match: \b never fires between G and B.
    // Cisco "UCS B200 M5" blades are stripped before matching.
    pattern: /\bB(100|200|300)\b/,
    platform: 'NVIDIA HGX B100/B200/B300 (8-GPU baseboard)',
    gpuUnits: 8,
    defaultGpuName: 'NVIDIA B200 SXM',
    source: 'NVIDIA HGX B200/B300 datasheets: 8 SXM GPUs per baseboard, the x86 server form factor',
  },
];

export interface GpuPlatformMatch {
  platform: string;
  gpuUnits: number;
  defaultGpuName: string;
  source: string;
}

type NamedRow = Pick<CarbonSourceRow, 'modelName' | 'displayName' | 'verifiedSku'>;

/**
 * SIL-2178: look a SKU's names (and, when known, its resolved GPU model
 * name, so a B200 known only from sku_oem_spec.gpu_model still hits the
 * Blackwell rule) up in GPU_PLATFORM_TABLE.
 */
export function resolveGpuPlatform(row: NamedRow, gpuName: string | null = null): GpuPlatformMatch | null {
  const texts = [row.modelName, row.displayName, row.verifiedSku.replace(/_/g, ' '), gpuName]
    .filter((t): t is string => typeof t === 'string' && t.trim() !== '')
    .map((t) => stripNonGpuModelCodes(splitGluedCount(t.toUpperCase())));
  for (const entry of GPU_PLATFORM_TABLE) {
    if (texts.some((t) => entry.pattern.test(t))) {
      const { platform, gpuUnits, defaultGpuName, source } = entry;
      return { platform, gpuUnits, defaultGpuName, source };
    }
  }
  return null;
}

/** SIL-2178: the first explicit GPU count across a SKU's names. */
export function resolveNamedGpuCount(row: NamedRow): number | null {
  return (
    extractGpuCountFromName(row.modelName) ??
    extractGpuCountFromName(row.displayName) ??
    extractGpuCountFromName(row.verifiedSku)
  );
}

/**
 * Obvious-mismatch detector for the pre-run GPU spot-check: the input name
 * and the archetype BoaviztAPI matched must not carry two DIFFERENT family
 * tokens (H100 must never map to A100). "Not found" is not a mismatch — it
 * is an explicit fall-through to the DEFAULT archetype, surfaced separately.
 */
export function isObviousGpuMismatch(inputName: string, matchedName: string): boolean {
  const a = extractGpuToken(inputName);
  const b = extractGpuToken(matchedName);
  if (!a || !b) return false;
  return a.replace(/\s/g, '') !== b.replace(/\s/g, '');
}

/**
 * SIL-2178 root cause: the ingestion Kysely pool (apps/ingestion/src/db.ts)
 * runs `new CamelCasePlugin()` with no `maintainNestedObjectKeys`, so it
 * rewrites keys INSIDE jsonb result values too. (The api pools set
 * `maintainNestedObjectKeys: true`, apps/api/src/lib/pinned-kysely.ts, except
 * privilegedDb, which keeps the default and camel-cases nested keys.) A
 * `sku.class_attributes` written as {"gpu_slots": 8, "cpu_sockets": 2}
 * therefore reaches the compute as {gpuSlots: 8, cpuSockets: 2}, and every
 * `attrs['gpu_slots']` read below returned undefined in prod. That is why
 * the 2026-10-20 sweep wrote AIVRES_KR9288_B200 with gpu.units = 1 despite a
 * numeric gpu_slots 8: the B200 token resolved the model, no slot count was
 * seen, and the old fallback assumed 1. Unit tests passed because their
 * fixtures used snake_case keys. Every class_attributes read here goes
 * through this view, which accepts both casings (snake_case wins when both
 * are present). Keys are flat scalars in the 'server' / 'gpu' /
 * 'rack_system' / 'memory' schemas, so only the top level is normalized.
 * The alternative for a loader that must see keys exactly as stored is the
 * SIL-1834 pair `rawJsonbText` / `parseRawJsonb` (packages/shared/src/
 * jsonb-raw.ts); normalizing here instead covers every caller of this pure
 * derivation (ingestion CLI, sweep, use-phase, the api carbon tester)
 * whatever pool configuration loaded the row.
 */
export function normalizeClassAttributes(attrs: Record<string, unknown> | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!attrs || typeof attrs !== 'object') return out;  for (const [key, value] of Object.entries(attrs)) {
    const snake = key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    if (snake === key || !(snake in attrs)) out[snake] = value;
  }
  return out;
}

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/** PSU unit count from a psu_redundancy class attribute (N / N+1 / 2N). */
function psuUnitsFromRedundancy(redundancy: string | null): number | null {
  if (!redundancy) return null;
  const r = redundancy.toUpperCase().replace(/\s/g, '');
  if (r === 'N') return 1;
  if (r === 'N+1' || r === '2N') return 2;
  return null;
}

// ── SIL-395: derived RAM configuration ───────────────────────────────────────
//
// max_ram_gb is a MAX-SUPPORTED ceiling, not an installed configuration —
// feeding it straight through overstates embodied carbon by roughly an order
// of magnitude (SIL-392 measured the old archetype-default behavior at a flat
// 8x32 GB = 256 GB for every server SKU). This derivation instead assumes a
// realistic INSTALLED fraction of the ceiling and reports a single module
// tier (`ram: [{ units, capacity }]`).
//
// All four constants below are named, commented, and co-located so they can
// be retuned together once real telemetry lands — n=0 today: no asset in
// production carries any RAM attribute, so RAM_FILL_RATIO in particular is a
// deliberately conservative guess, not a calibrated figure.

/**
 * Fraction of max_ram_gb assumed actually installed. NO calibration data
 * behind this constant (n=0 — no production asset carries a RAM attribute).
 * Because RAM is ~88-90% of server embodied GWP, this single number drives
 * ~90% of every server SKU's published embodied-carbon figure. Deliberately
 * conservative; revisit the instant real telemetry exists.
 */
export const RAM_FILL_RATIO = 0.25;

/**
 * Ceiling on installed RAM regardless of how large max_ram_gb is (GB). Caps
 * the tail of the catalog (max_ram_gb up to 256,256 GB) at a plausible
 * maximum production configuration rather than scaling the fill ratio
 * indefinitely.
 */
export const RAM_INSTALLED_GB_CAP = 6144;

/**
 * GB per module used to size the module COUNT (`round(installed_gb / this)`)
 * — a sizing divisor only, never presented as a specific DIMM capacity SKU.
 * Explicitly NOT a DDR5 RDIMM ladder ([16,32,64,96,128,192,256]) — SIL-392
 * killed that design: module-count precision is worth ~5.22 kgCO2eq/DIMM,
 * ~10% of this derivation's correction and entirely inside BoaviztAPI's own
 * min/max band, and BoaviztAPI rounds embodied GWP to TWO SIGNIFICANT
 * FIGURES, so at >= 4096 GB that precision is unrepresentable in the value
 * we persist. Do not reintroduce a ladder or snap table.
 */
export const RAM_MODULE_SIZING_DIVISOR_GB = 64;

/**
 * Fallback DIMM slot count when sku_oem_spec.dimm_slots is NULL (expected
 * for every SKU until the dimm_slots scrape backfill runs — SIL-393). Caps
 * the module count the sizing divisor would otherwise imply (e.g. 6144 GB /
 * 64 = 96 modules, which no chassis holds).
 */
export const RAM_ASSUMED_DIMM_SLOTS = 32;

/**
 * SIL-413: `installed_gb / units` does not divide evenly in general (e.g.
 * NVIDIA_DGX_STATION_GB300: max_ram_gb=748, no dimm_slots -> installed_gb=187,
 * units=3, 187/3 = 62.33 GB) and BoaviztAPI's `configuration.ram[].capacity`
 * is declared `int` — a fractional module capacity is a 422 there, and it is
 * physically meaningless regardless: no 62.33 GB DIMM exists.
 *
 * The invariant this derivation exists to protect is TOTAL INSTALLED RAM
 * (`installed_gb`), not module count — `installed_gb` is the figure the
 * whole RAM_FILL_RATIO/RAM_INSTALLED_GB_CAP model was built to estimate, and
 * it is what dominates the embodied-carbon output (RAM is ~88-90% of a
 * server's embodied GWP). `units` is a means to size a plausible module tier
 * for BoaviztAPI's `[{units, capacity}]` shape, not an end in itself.
 * Therefore this rounds `capacity` to the nearest integer GB and leaves
 * `units` alone, rather than adjusting `units` to force an even division —
 * changing `units` to chase integer capacity would perturb the number the
 * derivation is actually trying to get right, to fix a display artifact of
 * a number nobody claims is measured precisely anyway.
 *
 * This DOES let `units * capacity` drift from `installed_gb` by rounding
 * error — bounded to at most 0.5 GB per module, i.e. well under 1% of
 * `installed_gb` at any module count this derivation produces (units is
 * capped at `RAM_ASSUMED_DIMM_SLOTS` / a scraped `dimm_slots`, both small).
 * That drift is immaterial: BoaviztAPI itself rounds embodied GWP to two
 * significant figures, and RAM_MODULE_SIZING_DIVISOR_GB's own docblock
 * already establishes that sub-DIMM precision here is unrepresentable in
 * the persisted value.
 *
 * Deliberately NOT snapped to a standard DIMM ladder (16/32/64/96/128/192/
 * 256 GB, same table as STANDARD_DIMM_CAPACITIES_GB above) even though a
 * snapped value would read as a more "real" module size (e.g. 62 -> 64):
 * snapping moves `units * capacity` further from `installed_gb` than plain
 * rounding does, and RAM_MODULE_SIZING_DIVISOR_GB's docblock already
 * rejected a ladder/snap table on the same total-RAM-precision grounds
 * (SIL-392). Extending the same reasoning here rather than re-litigating it:
 * an integer is all the invariant (and BoaviztAPI's schema) requires: it does
 * not require the specific integer to be a real DIMM SKU.
 */
function integerModuleCapacityGb(totalGb: number, units: number): number {
  return Math.max(1, Math.round(totalGb / units));
}

// ── SIL-413: soldered-memory machines are a DIFFERENT CLASS, not a variant
// of the DIMM-slot estimate ──────────────────────────────────────────────────
//
// NVIDIA_DGX_STATION_GB300 has an NVIDIA Grace CPU (Neoverse V2 cores) with
// soldered LPDDR5X — `dimm_slots` is legitimately NULL on every capture, not
// unscraped: there is no DIMM topology to count. Three consequences follow
// from "the memory is soldered, not expandable":
//
//   1. installed = max_ram_gb EXACTLY. RAM_FILL_RATIO models a buyer CHOOSING
//      how many of N slots to populate — soldered memory offers no such
//      choice; the shipped chip capacity is the only capacity that will ever
//      exist on the board. Applying the 0.25 fill ratio here (as the general
//      derivation below does) understates a Grace-class SKU's installed RAM
//      by 4x, not a small correction.
//   2. RAM_ASSUMED_DIMM_SLOTS (32) must NOT apply. That constant caps a
//      module count sized off a divisor for a chassis that has slots to
//      cap — using it on a soldered board invents a DIMM topology for a
//      machine with none, which is exactly how 748 GB / assumed-32-slot
//      arithmetic produced 3 modules of 62.33 GB in the original bug report.
//   3. The BoaviztAPI payload becomes a single `{units: 1, capacity: total}`
//      block: there is one soldered memory subsystem, not N modules. This is
//      right on the merits, not merely a convenient collapse — SIL-392
//      measured a fixed ~5.22 kgCO2eq PER-MODULE term in BoaviztAPI's RAM
//      model (DIMM packaging/manufacturing overhead), which has no soldered-
//      chip analog; charging that per-module overhead N times for hardware
//      that was never assembled from N modules would overstate embodied
//      carbon, on top of the fill-ratio understatement in (1).
//
// This is the OPPOSITE correction from PR #849's Dell-gate widening to 30x:
// that widening rests on manufacturer PCFs being measured at a MINIMUM TEST
// configuration that a real DIMM-slot buyer exceeds by populating more
// slots — true for expandable memory, false here. A soldered board ships in
// exactly one memory configuration, so its manufacturer PCF already reflects
// the full, only, shipped memory — our modeled figure should MATCH the PCF
// for this class, not exceed it the way the general DIMM-slot estimate does.
//
// Detection — investigated against production data before settling on this
// classifier; two other signals were considered and rejected:
//
//   - `raw_payload->>'memory_type'` (154 rows scraped: DDR5/DDR4/LPDDR/GPU-
//     HBM values) LOOKS like the natural signal but is not usable today: the
//     TYPED `memory_type` column has only 32 rows and they are exactly the
//     GPU HBM/GDDR values — the writer drops every server-class value (the
//     same field-mapping gap SIL-409 fixed for cpu_model/gpu_model, still
//     open for this one; a separate bug, not fixed here). Even once that
//     writer gap is fixed, `LPDDR` only shows up on `rack_system`/
//     `cloud_vm`-class SKUs (GB200/GB300 NVL72 racks, Azure ND128ISR) — NOT
//     on NVIDIA_DGX_STATION_GB300 itself, which has no LPDDR mention in any
//     capture. memory_type would not have classified the SKU that actually
//     failed even if the writer gap were fixed.
//   - `dimm_slots IS NULL` is NOT usable: it is equally true of any SKU
//     simply not yet scraped for dimm_slots (SIL-393's backfill is
//     ongoing), so it cannot distinguish "no slots exist" from "not
//     captured yet".
//
// What DOES work: `sku_oem_spec.cpu_model` (a structured field that only
// started being persisted with SIL-409 — before that this SKU's CPU
// identity was not captured at all, which is why this classifier was not
// possible until now). Grace is an architectural fact, not a per-SKU
// inference: NVIDIA's Grace CPU is ALWAYS paired with soldered on-package
// LPDDR5X by design — there is no slotted-DIMM Grace SKU, unlike x86
// Xeon/EPYC where one family name spans many board designs. Matching
// cpu_model against "Grace" is therefore a sound family-level classifier,
// not a guess about one machine.
//
// Deliberately NOT matching the bare "Neoverse V2" core name (an earlier
// draft of this classifier did): Neoverse V2 is a licensed Arm CORE DESIGN
// used by multiple vendors — e.g. AWS Graviton4 is also Neoverse V2, with
// conventional slotted DDR5, not soldered memory. Matching the core name
// would misclassify any future Neoverse-V2-based CPU that is not Grace.
// Every Neoverse V2 cpu_model string in the catalog today also contains
// "Grace" ("Grace", "Grace 72-Core Neoverse V2", "NVIDIA Grace 72-Core
// Neoverse V2"), so the narrower "Grace"-only pattern loses no coverage.
// Extend this list only on the same evidence bar — a confirmed CPU FAMILY
// that is soldered-memory by design, verified against real captures —
// never generalized to "any Arm CPU" or "any CPU with no dimm_slots".
//
// Deliberately matches `cpu_model` ONLY, never `cpu_family_text` (the
// unstructured scrape prose) — cpu_family_text was not part of the
// production investigation behind this classifier, so its reliability for
// this purpose is unverified.
//
// Guarded fallback: `cpu_model` is NULL on the majority of server SKUs
// today (94 of 148 — cpu_model coverage is new, SIL-409-era) — those rows
// CANNOT be classified by this signal and MUST fall through to the
// existing slotted-DIMM estimate path unchanged, never default to
// "soldered". A wrong classification silently swaps in a 4x-different fill
// ratio (1.0 vs 0.25) on what might be a real DIMM-slot server, which is a
// much worse failure mode than leaving a genuinely-soldered SKU on the
// already-existing, already-accepted DIMM-slot estimate. `isSolderedMemoryCpu`
// returns `false` for `null`, and `deriveServerConfig` only takes the
// soldered branch on a POSITIVE match — everything else, including
// "unknown", stays on `deriveRamConfiguration`.
//
// NVIDIA_HGX_H100 (a GPU baseboard with no memory subsystem of its own) does
// NOT need this path: it carries no max_ram_gb signal at all, so it already
// resolves correctly via the existing `archetype_default` branch below (no
// ceiling to misapply a fill ratio to).
//
// Also flagged, NOT resolved here: this SKU's max_ram_gb itself scraped
// inconsistently — 496 GB (twice) and 748 GB (twice). 496 looks like the
// Grace LPDDR5X system-memory figure; 748 may conflate system and GPU
// memory, or be a scrape error. At RAM_FILL_RATIO=0.25 a 4x-off max_ram_gb
// only moved the estimate by a fraction of that; at fill ratio 1.0 (this
// path) the raw max_ram_gb value passes straight through, so which of
// 496/748 is correct now matters far more. Resolving that scrape
// inconsistency is a dependency for trusting this SKU's actual figure, not
// something this ticket's integer-capacity/soldered-classification fix
// attempts to adjudicate.

/**
 * CPU families confirmed, against production `cpu_model` captures, to be
 * soldered-memory BY DESIGN (no slotted-DIMM variant exists). NVIDIA Grace
 * is the only confirmed entry today — see the module-header note above for
 * why this list is deliberately narrow (matches `cpu_model` only, never the
 * bare Neoverse core name or the unstructured `cpu_family_text`) and must
 * stay that way: extend only on the same "family is soldered by design,
 * verified against real captures" evidence bar, never generalized.
 */
const SOLDERED_MEMORY_CPU_FAMILIES: readonly RegExp[] = [/\bGRACE\b/i];

/**
 * Whether a SKU's `cpu_model` identifies a CPU family that is soldered
 * (non-DIMM) system memory BY DESIGN — see the SIL-413 module-header note
 * for the evidence behind this and why it is deliberately narrow.
 *
 * `cpuModel == null` (94 of 148 server SKUs today — cpu_model coverage is
 * new, SIL-409-era) returns `false`, NOT a guess: an unclassifiable SKU
 * must fall through to the existing slotted-DIMM estimate, never default to
 * the soldered path — see the module-header note on the guarded fallback.
 *
 * @pure
 */
export function isSolderedMemoryCpu(cpuModel: string | null): boolean {
  if (cpuModel == null) return false;
  return SOLDERED_MEMORY_CPU_FAMILIES.some((re) => re.test(cpuModel));
}

/**
 * Derive an installed RAM configuration for a soldered-memory SKU (SIL-413):
 * `installed_gb = max_ram_gb` exactly (no RAM_FILL_RATIO — see the
 * module-header note for why a fill ratio does not apply to non-expandable
 * memory), reported as a single `{units: 1, capacity}` block (no
 * RAM_ASSUMED_DIMM_SLOTS-driven module count — there is no DIMM topology to
 * size). `capacity` is still integer-rounded via `integerModuleCapacityGb`
 * for the same reason as the general derivation: BoaviztAPI's schema and
 * physical reality both require an integer.
 *
 * @pure
 */
export function deriveSolderedRamConfiguration(maxRamGb: number): RamDerivation {
  const capacity = integerModuleCapacityGb(maxRamGb, 1);
  return {
    ram: [{ units: 1, capacity }],
    provenanceKind: 'soldered_exact',
    message:
      `soldered memory: installed ${capacity} GB = max_ram_gb=${maxRamGb} exactly (RAM_FILL_RATIO ` +
      'does not apply — the memory is not expandable, so there is no partially-populated ' +
      'configuration to model), reported as a single unit=1 block (no DIMM topology on a ' +
      'soldered board; BoaviztAPI\'s per-module packaging-overhead term does not apply here). ' +
      'Unlike the general DIMM-slot estimate, this SKU\'s modeled figure is expected to MATCH ' +
      'its manufacturer PCF, not exceed it — a soldered board ships in exactly one memory ' +
      "configuration, so the PCF's test configuration already IS the shipped configuration.",
    dimmSlotsGuardMessage: null,
  };
}

/**
 * How a RAM configuration/provenance was reached (SIL-395; 'measured' added
 * by SIL-396; 'soldered_exact' added by SIL-413). Precedence ladder, highest
 * first:
 *   measured                 -- asset.class_attributes.installed_ram_gb (rung 1)
 *   soldered_exact           -- SKU max_ram_gb taken exactly, no fill ratio, no
 *                               DIMM slots (rung 2 — see deriveSolderedRamConfiguration)
 *   estimated_scraped_slots  -- SKU max_ram_gb x fill ratio, scraped dimm_slots (rung 2)
 *   estimated_assumed_slots  -- SKU max_ram_gb x fill ratio, assumed slot count (rung 2)
 *   archetype_default        -- no ceiling available at all (rung 3)
 */
export type RamProvenanceKind =
  | 'measured'
  | 'soldered_exact'
  | 'estimated_scraped_slots'
  | 'estimated_assumed_slots'
  | 'archetype_default';

export interface RamDerivation {
  /** `undefined` only for `archetype_default` (no max_ram_gb signal at all). */
  ram: Array<{ units: number; capacity: number }> | undefined;
  provenanceKind: RamProvenanceKind;
  /** Human-readable defaulted_fields['ram'] message — see module rules:
   * NEVER phrase an estimate as measured. */
  message: string;
  /**
   * SIL-402 plausibility guard (defence in depth, NOT a substitute for
   * row-coherent spec selection — it cannot catch a wrong-but-plausible
   * pair, only an implausible one). Non-null exactly when a scraped
   * `dimm_slots` was present but implied a non-standard per-DIMM capacity
   * (max_ram_gb / dimm_slots not in {16,32,64,96,128,256} GB) and was
   * therefore ignored in favor of `RAM_ASSUMED_DIMM_SLOTS`. Recorded under
   * its OWN defaulted_fields key ('ram.dimm_slots_plausibility'), separate
   * from the main 'ram' message, so the guard firing is independently
   * visible/queryable.
   */
  dimmSlotsGuardMessage: string | null;
}

/**
 * Standard DIMM capacities (GB) a `max_ram_gb / dimm_slots` implied per-slot
 * figure is checked against. Not exhaustive of every DIMM ever shipped —
 * just the common ladder — so a false-positive guard trip (rejecting a
 * real but unusual DIMM size) is the accepted failure mode over silently
 * trusting an impossible one (SIL-402: a measured 24 slots / 8192 GB pair
 * implied a 341 GB DIMM).
 */
// 192 is included deliberately: 192 GB RDIMMs ship in current Xeon 6 / EPYC
// systems, and a 32-slot 6144 GB chassis implies exactly 192 — without it the
// guard would discard a valid scraped slot count. No SKU implies 192 in the
// catalogue today (32/64/128/256 only), so this is a latent false positive
// rather than an active one. Keep in sync with STANDARD_DIMM_SIZES_GB in
// apps/operations/src/routes/skus_.$skuId.tsx (SIL-403's suspect badge).
const STANDARD_DIMM_CAPACITIES_GB: readonly number[] = [16, 32, 64, 96, 128, 192, 256];

function isStandardDimmCapacityGb(gb: number): boolean {
  return STANDARD_DIMM_CAPACITIES_GB.some((c) => Math.abs(gb - c) < 0.01);
}

/**
 * Derive an installed RAM configuration from a SKU's max-supported RAM
 * ceiling. Pure.
 *
 *   installed_gb = min(max_ram_gb * RAM_FILL_RATIO, RAM_INSTALLED_GB_CAP)
 *   units        = min(round(installed_gb / RAM_MODULE_SIZING_DIVISOR_GB),
 *                       dimm_slots ?? RAM_ASSUMED_DIMM_SLOTS)
 *   capacity     = round(installed_gb / units)   -- SIL-413: always an integer,
 *                                                    see `integerModuleCapacityGb`
 *
 * Returns `archetype_default` (no `ram` field) when max_ram_gb is null — the
 * only case where BoaviztAPI's generic archetype still applies.
 *
 * @pure
 */
export function deriveRamConfiguration(
  maxRamGb: number | null,
  dimmSlots: number | null,
): RamDerivation {
  if (maxRamGb == null) {
    return {
      ram: undefined,
      provenanceKind: 'archetype_default',
      message:
        'archetype_default (no max_ram_gb on active OEM spec) — BoaviztAPI\'s generic RAM ' +
        'archetype applies; treat this SKU\'s embodied figure as understated relative to SKUs ' +
        'with a derived configuration',
      dimmSlotsGuardMessage: null,
    };
  }

  // SIL-402 plausibility guard (defence in depth only — see RamDerivation's
  // docblock for why this cannot replace row-coherent spec selection): a
  // scraped dimm_slots is trusted only when max_ram_gb / dimm_slots implies
  // a standard per-DIMM capacity. An implausible implied size (e.g. a
  // wrongly-paired 8192 GB / 24 slots = 341 GB/DIMM) is ignored in favor of
  // the RAM_ASSUMED_DIMM_SLOTS fallback, same as if dimm_slots were NULL.
  let effectiveDimmSlots = dimmSlots;
  let dimmSlotsGuardMessage: string | null = null;
  if (dimmSlots != null && dimmSlots > 0) {
    const impliedPerDimmGb = maxRamGb / dimmSlots;
    if (!isStandardDimmCapacityGb(impliedPerDimmGb)) {
      dimmSlotsGuardMessage =
        `ignored scraped dimm_slots=${dimmSlots} (max_ram_gb=${maxRamGb} / dimm_slots=${dimmSlots} = ` +
        `${Math.round(impliedPerDimmGb * 100) / 100} GB/DIMM, not a standard DIMM capacity — ` +
        `{${STANDARD_DIMM_CAPACITIES_GB.join(', ')}} GB) — falling back to the assumed ` +
        `${RAM_ASSUMED_DIMM_SLOTS}-slot chassis. Defence in depth only: this catches an ` +
        'implausible pairing, not a wrong-but-plausible one — see SIL-402.';
      effectiveDimmSlots = null;
    }
  }

  const installedGb = Math.min(maxRamGb * RAM_FILL_RATIO, RAM_INSTALLED_GB_CAP);
  const slotsCap = effectiveDimmSlots ?? RAM_ASSUMED_DIMM_SLOTS;
  const rawUnits = Math.round(installedGb / RAM_MODULE_SIZING_DIVISOR_GB);
  const units = Math.max(1, Math.min(rawUnits, slotsCap));
  const capacity = integerModuleCapacityGb(installedGb, units);
  const ram = [{ units, capacity }];

  const scraped = effectiveDimmSlots != null;
  const provenanceKind: RamProvenanceKind = scraped ? 'estimated_scraped_slots' : 'estimated_assumed_slots';
  const slotsClause = scraped
    ? `scraped dimm_slots=${effectiveDimmSlots}`
    : `no dimm_slots on record — assumed ${RAM_ASSUMED_DIMM_SLOTS}-slot chassis`;

  const message =
    `estimated ${units} x ${capacity} GB (${installedGb} GB installed, ${slotsClause}) from ` +
    `max_ram_gb=${maxRamGb} (max SUPPORTED capacity) x RAM_FILL_RATIO=${RAM_FILL_RATIO} — ` +
    'a modeled realistic production configuration, not a measured value. Manufacturer PCFs ' +
    'are typically published at minimum test configurations (e.g. 64 GB installed in an 8 TB-' +
    'capable chassis); this figure being a large multiple of a published PCF is expected and ' +
    'correct, not an error.';

  return { ram, provenanceKind, message, dimmSlotsGuardMessage };
}

// ── SIL-396: measured (rung 1) RAM configuration ─────────────────────────────
//
// asset.class_attributes.installed_ram_gb (716_server_asset_class_schema_v1.sql)
// is a REPORTED installed figure, not a ceiling to be fractioned down — unlike
// deriveRamConfiguration above, no RAM_FILL_RATIO / RAM_INSTALLED_GB_CAP
// applies. The module-count sizing divisor and dimm-slot cap are still
// applied (same RAM_MODULE_SIZING_DIVISOR_GB / RAM_ASSUMED_DIMM_SLOTS as the
// estimated rung) purely to size a plausible module tier for BoaviztAPI's
// `ram: [{units, capacity}]` shape — never to second-guess the reported total.

export interface MeasuredRamDerivation {
  ram: Array<{ units: number; capacity: number }>;
  provenanceKind: 'measured';
  message: string;
}

/**
 * Derive an installed RAM configuration from a MEASURED per-asset
 * installed_ram_gb (rung 1 of the precedence ladder). Pure.
 *
 * @pure
 */
export function deriveMeasuredRamConfiguration(
  installedRamGb: number,
  dimmSlots: number | null,
): MeasuredRamDerivation {
  const slotsCap = dimmSlots ?? RAM_ASSUMED_DIMM_SLOTS;
  const rawUnits = Math.round(installedRamGb / RAM_MODULE_SIZING_DIVISOR_GB);
  const units = Math.max(1, Math.min(rawUnits, slotsCap));
  const capacity = integerModuleCapacityGb(installedRamGb, units);

  return {
    ram: [{ units, capacity }],
    provenanceKind: 'measured',
    message:
      `measured ${units} x ${capacity} GB (${installedRamGb} GB installed) from ` +
      `asset.class_attributes.installed_ram_gb=${installedRamGb} -- the asset's own reported ` +
      'installed configuration (rung 1 of the precedence ladder), not an estimate.',
  };
}

// ── SIL-197: CPU identity signal ─────────────────────────────────────────────

/** BoaviztAPI CPU-database family names we can map catalog text onto. */
export type CpuFamily =
  | 'Skylake'
  | 'Cascade Lake'
  | 'Ice Lake'
  | 'Sapphire Rapids'
  | 'Naples'
  | 'Rome'
  | 'Milan'
  | 'Genoa';

/**
 * Mainstream representative CPU per family — every name verified present in
 * the pinned BoaviztAPI 2.3.0 /v1/utils/cpu_name vocabulary. Used when the
 * catalog only knows the platform family ("4th Gen Intel Xeon Scalable"),
 * not a specific model. Mid-range volume parts, deliberately not flagships,
 * so family-level resolution does not overstate die area.
 */
export const CPU_FAMILY_REPRESENTATIVES: Record<CpuFamily, string> = {
  Skylake: 'Intel Xeon Gold 6148',
  'Cascade Lake': 'Intel Xeon Gold 6248',
  'Ice Lake': 'Intel Xeon Gold 6330',
  'Sapphire Rapids': 'Intel Xeon Gold 6430',
  Naples: 'AMD EPYC 7601',
  Rome: 'AMD EPYC 7452',
  Milan: 'AMD EPYC 7543',
  Genoa: 'AMD EPYC 9354',
};

export interface CpuSignal {
  raw: string;
  vendor: 'Intel' | 'AMD' | null;
  /** Explicit CPU model candidate (e.g. "Xeon Platinum 8480+", "EPYC 9654"). */
  explicitModel: string | null;
  /** BoaviztAPI family implied by the text; null when indeterminable. */
  family: CpuFamily | null;
}

/**
 * Intel generation → BoaviztAPI family. Emerald Rapids (5th Gen) is not in
 * the 2.3.0 CPU database; it is a Sapphire Rapids refresh on the same
 * process, so it maps onto Sapphire Rapids. Ranges ("4th and 5th Gen") take
 * the LOWER generation — conservative for embodied estimates.
 */
const INTEL_FAMILY_TOKENS: Array<[RegExp, CpuFamily]> = [
  [/SKYLAKE|1ST\s*GEN/, 'Skylake'],
  [/CASCADE\s*LAKE|2ND(?:\s*(?:\/|AND|&|OR)\s*3RD)?\s*GEN/, 'Cascade Lake'],
  [/ICE\s*LAKE|3RD\s*GEN/, 'Ice Lake'],
  [/SAPPHIRE\s*RAPIDS|4TH(?:\s*(?:\/|AND|&|OR)\s*5TH)?\s*GEN/, 'Sapphire Rapids'],
  [/EMERALD\s*RAPIDS|5TH\s*GEN/, 'Sapphire Rapids'],
];

/**
 * AMD EPYC series → BoaviztAPI family. Turin (9005) is absent from the
 * 2.3.0 database and maps onto Genoa (same SP5 platform, closest die
 * model). Series ranges (7002/7003) take the older series.
 */
const AMD_FAMILY_TOKENS: Array<[RegExp, CpuFamily]> = [
  [/NAPLES|7001|1ST\s*GEN/, 'Naples'],
  [/ROME|7002|2ND(?:\s*(?:\/|AND|&|OR)\s*3RD)?\s*GEN/, 'Rome'],
  [/MILAN|7003|3RD\s*GEN/, 'Milan'],
  [/GENOA|9004|4TH(?:\s*(?:\/|AND|&|OR)\s*5TH)?\s*GEN/, 'Genoa'],
  [/TURIN|9005|5TH\s*GEN/, 'Genoa'],
];

const XEON_MODEL_RE = /XEON[\s-]+((?:SCALABLE\s+)?(?:PLATINUM|GOLD|SILVER|BRONZE|MAX|W|E|D)[\s-]+\d{4}[A-Z+]*)/i;
const EPYC_MODEL_RE = /EPYC[\s-]+(\d{4}[A-Z]*)\b/i;

/**
 * EPYC SERIES numbers ("AMD EPYC 9004 Series") — platform generations, not
 * part numbers. They must resolve via the family path, never as an explicit
 * model.
 */
const EPYC_SERIES_NUMBERS = new Set(['7001', '7002', '7003', '9004', '9005']);

/**
 * Parse free-text CPU identity (spec cpu_model or scraped processor-family
 * prose) into a resolvable signal. Returns null when the text names no CPU
 * vendor, or names BOTH vendors without an explicit model ("AMD EPYC or
 * Intel Xeon, varies by configuration") — ambiguous identity must stay an
 * archetype default rather than guess a vendor.
 */
export function parseCpuSignal(text: string | null | undefined): CpuSignal | null {
  if (!text || text.trim() === '') return null;
  const raw = text.trim();
  const up = raw.toUpperCase();

  const mentionsIntel = /XEON|INTEL/.test(up);
  const mentionsAmd = /EPYC|\bAMD\b/.test(up);

  const xeon = XEON_MODEL_RE.exec(raw);
  let epyc = EPYC_MODEL_RE.exec(raw);
  if (epyc && EPYC_SERIES_NUMBERS.has(epyc[1]!.toUpperCase())) epyc = null;
  if (xeon && !epyc) {
    return {
      raw,
      vendor: 'Intel',
      explicitModel: `Intel Xeon ${xeon[1]!.replace(/\s+/g, ' ').replace(/^SCALABLE\s+/i, '').trim()}`,
      family: matchFamily(up, INTEL_FAMILY_TOKENS),
    };
  }
  if (epyc && !xeon) {
    return {
      raw,
      vendor: 'AMD',
      explicitModel: `AMD EPYC ${epyc[1]!.toUpperCase()}`,
      family: matchFamily(up, AMD_FAMILY_TOKENS),
    };
  }

  if (mentionsIntel && mentionsAmd) return null; // either-vendor config text
  if (mentionsIntel) return { raw, vendor: 'Intel', explicitModel: null, family: matchFamily(up, INTEL_FAMILY_TOKENS) };
  if (mentionsAmd) return { raw, vendor: 'AMD', explicitModel: null, family: matchFamily(up, AMD_FAMILY_TOKENS) };
  return null;
}

function matchFamily(upperText: string, tokens: Array<[RegExp, CpuFamily]>): CpuFamily | null {
  for (const [re, family] of tokens) {
    if (re.test(upperText)) return family;
  }
  return null;
}

/**
 * Cross-family guard for CPU archetype resolution (SIL-197), mirroring the
 * GPU guard: BoaviztAPI's fuzzy matcher maps "4th Gen Intel Xeon Scalable"
 * to a Skylake part, so a candidate match is rejected when:
 *   - the matched part's vendor differs from the signal's vendor, or
 *   - the signal implies a family and the match belongs to a different one, or
 *   - the signal names an explicit model number the match does not carry.
 */
export function isObviousCpuMismatch(
  signal: CpuSignal,
  matched: { name: string; family?: string | null },
): boolean {
  const matchedUp = matched.name.toUpperCase();
  const matchedVendor = /XEON|INTEL/.test(matchedUp) ? 'Intel' : /EPYC|\bAMD\b/.test(matchedUp) ? 'AMD' : null;
  if (signal.vendor && matchedVendor && signal.vendor !== matchedVendor) return true;
  if (signal.family && matched.family) {
    // BoaviztAPI family labels carry platform suffixes ("Ice Lake-SP") —
    // compare on normalized containment, not equality.
    const a = signal.family.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const b = matched.family.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!b.includes(a) && !a.includes(b)) return true;
  }
  if (signal.explicitModel) {
    const modelNum = /(\d{4}[A-Z+]*)/.exec(signal.explicitModel.toUpperCase())?.[1];
    if (modelNum && !matchedUp.includes(modelNum)) return true;
  }
  return false;
}

/**
 * Ordered resolution candidates for a CPU signal: the explicit model first
 * (exact part number wins), then the family's mainstream representative.
 */
export function cpuResolutionCandidates(signal: CpuSignal): string[] {
  const out: string[] = [];
  if (signal.explicitModel) out.push(signal.explicitModel);
  if (signal.family) {
    const rep = CPU_FAMILY_REPRESENTATIVES[signal.family];
    if (rep !== signal.explicitModel) out.push(rep);
  }
  return out;
}

function deriveServerConfig(
  row: CarbonSourceRow,
  installedRamGb: number | null,
): {
  server: ServerConfigurationPayload;
  cpuNameCandidate: string | null;
  defaulted: Record<string, string>;
  ramProvenanceKind: RamProvenanceKind;
} {
  const attrs = normalizeClassAttributes(row.classAttributes);
  const defaulted: Record<string, string> = {};

  const cpuUnits = row.cpuSocketCount ?? row.cpuMaxSockets ?? num(attrs['cpu_sockets']);
  const cpuNameCandidate = str(row.cpuModel) ?? str(row.cpuFamilyText);
  const cpuCores = row.cpuCores;

  const cpu: NonNullable<ServerConfigurationPayload['configuration']['cpu']> = {};
  if (cpuUnits != null) cpu.units = cpuUnits;
  else defaulted['cpu.units'] = 'archetype_default (no socket count on spec or class_attributes)';
  // SIL-197: cpu.name is set by the compute CLI only after a guarded
  // BoaviztAPI vocabulary resolution of cpuNameCandidate. With no candidate
  // at all the default is known now.
  if (!cpuNameCandidate) {
    defaulted['cpu.name'] = 'archetype_default (no cpu_model / processor-family text on OEM specs)';
  }
  if (cpuCores != null) cpu.core_units = cpuCores;
  else defaulted['cpu.core_units'] = 'archetype_default (no cpu_cores on active OEM spec)';

  // SIL-396: rung 1 (measured) overrides the SKU ceiling-derived estimate
  // (rung 2) / archetype default (rung 3) whenever the ASSET carries its own
  // installed_ram_gb. A measured value is provenance, not a default — it is
  // NEVER folded into `defaulted['ram']` (mirrors the gpu/cpu archetype
  // resolution convention: only the unresolved fallback belongs there).
  let ram: Array<{ units: number; capacity: number }> | undefined;
  let ramProvenanceKind: RamProvenanceKind;
  if (installedRamGb != null) {
    const measured = deriveMeasuredRamConfiguration(installedRamGb, row.dimmSlots);
    ram = measured.ram;
    ramProvenanceKind = measured.provenanceKind;
  } else if (row.maxRamGb != null && isSolderedMemoryCpu(row.cpuModel)) {
    // SIL-413: soldered-memory CPU (e.g. NVIDIA Grace) — a different class
    // from the DIMM-slot estimate below, not a variant of it. See the
    // module-header note above deriveSolderedRamConfiguration for why
    // RAM_FILL_RATIO / RAM_ASSUMED_DIMM_SLOTS must NOT apply here.
    const soldered = deriveSolderedRamConfiguration(row.maxRamGb);
    ram = soldered.ram;
    ramProvenanceKind = soldered.provenanceKind;
    defaulted['ram'] = soldered.message;
  } else {
    const ramDerivation = deriveRamConfiguration(row.maxRamGb, row.dimmSlots);
    ram = ramDerivation.ram;
    ramProvenanceKind = ramDerivation.provenanceKind;
    defaulted['ram'] = ramDerivation.message;
    if (ramDerivation.dimmSlotsGuardMessage) {
      defaulted['ram.dimm_slots_plausibility'] = ramDerivation.dimmSlotsGuardMessage;
    }
  }
  defaulted['disk'] = 'archetype_default (no per-SKU storage configuration data)';

  const psuUnits = psuUnitsFromRedundancy(str(attrs['psu_redundancy']));
  const configuration: ServerConfigurationPayload['configuration'] = { cpu };
  if (ram) configuration.ram = ram;
  if (psuUnits != null) configuration.power_supply = { units: psuUnits };
  else defaulted['power_supply.units'] = 'archetype_default (no psu_redundancy class attribute)';

  // Case type: blade only when the OEM form factor explicitly says so;
  // rack when we have any rack signal; otherwise rack is also BoaviztAPI's
  // own default, but we record that we could not confirm it.
  const formFactor = (row.formFactor ?? '').toLowerCase();
  const rackSignal = row.rackUnits != null || num(attrs['rack_units']) != null;
  const caseType = formFactor.includes('blade') ? 'blade' : 'rack';
  if (caseType === 'rack' && !rackSignal && !formFactor.includes('rack')) {
    defaulted['case.type'] = 'rack assumed (no rack_units / form factor signal)';
  }

  return { server: { model: { type: caseType }, configuration }, cpuNameCandidate, defaulted, ramProvenanceKind };
}

/** GPU model name for a gpu-class SKU or a gpu-bearing server. */
export function resolveGpuName(row: CarbonSourceRow): string | null {
  const attrs = normalizeClassAttributes(row.classAttributes);
  return (
    str(attrs['gpu_model']) ??
    str(row.gpuModel) ??
    extractGpuToken(row.modelName) ??
    extractGpuToken(row.displayName) ??
    extractGpuToken(row.verifiedSku.replace(/_/g, ' '))
  );
}

/**
 * defaulted_fields entries for the GPU archetype resolution outcome
 * (SIL-196). Only the unresolved DEFAULT fallback is a genuine default. A
 * successful match (e.g. "NVIDIA H100 SXM 80GB") is provenance, not a
 * default — it must NOT appear in defaulted_fields (the claim gate treats
 * 'gpu.archetype' as unresolved model identity and suppresses). The matched
 * archetype's params are already recorded in input_payload as
 * gpu.component_body by the compute CLI.
 */
export function gpuArchetypeDefaultedFields(
  matchedArchetypeName: string | null,
): Record<string, string> {
  if (matchedArchetypeName !== null) return {};
  return { 'gpu.archetype': 'DEFAULT (name not in BoaviztAPI GPU database)' };
}

/**
 * defaulted_fields entries for the CPU name resolution outcome (SIL-197),
 * following the SIL-196 provenance rule: a successful resolution is
 * provenance (recorded in input_payload as cpu_resolution by the compute
 * CLI), never a defaulted field. Only the unresolved fallback keeps
 * 'cpu.name' in defaulted_fields — which the claim gate treats as
 * unresolved model identity.
 */
export function cpuArchetypeDefaultedFields(
  resolvedName: string | null,
  candidateText: string,
): Record<string, string> {
  if (resolvedName !== null) return {};
  return {
    'cpu.name': `archetype_default (unresolved CPU text: ${candidateText.slice(0, 120)})`,
  };
}

// ── SIL-433: component-grain cpu/memory SKUs ─────────────────────────────────
//
// Spec §2/§9 (sustainability-forecasting-spec.md): the buy picker's cpu/
// memory classes read coverage dynamically off v_sku_carbon_footprint_latest,
// so landing these rows is a pure DATA prerequisite — no new compute path,
// the SAME BoaviztAPI component calls (/v1/component/cpu, /v1/component/ram)
// already made INSIDE the server derivation above (gpu_server composition
// already sums a component call the same way — see composeGpuServer in
// compute-carbon-footprints.ts). A standalone cpu/memory SKU is computed
// alone, not folded into a server chassis.

/**
 * Derive a standalone cpu SKU's BoaviztAPI component input. Reuses the SAME
 * guarded CPU name resolution as deriveServerConfig (the compute CLI's
 * resolveCpuArchetype against BoaviztAPI's /v1/utils/name_to_cpu vocabulary,
 * cross-family guarded) — cpuNameCandidate here is the free-text signal, the
 * CLI resolves it exactly like the server path, never a second resolution
 * scheme. `core_units` is the ONE spec-provided override (the CPU catalog's
 * own known cpu_cores — see 529_import_cpu_skus.sql), mirroring
 * applyResolvedCpu's "spec core count wins over archetype part's core
 * count" rule. `tdp` is deliberately NEVER spec-sourced here — the existing
 * server path has the same property (CarbonSourceRow carries no tdp_watts
 * field at all), so cpu tdp always comes from the resolved archetype, never
 * an invented default; this ticket does not widen that rule just because a
 * tdp_watts column happens to exist for this one asset class.
 *
 * @pure
 */
function deriveCpuComponentInput(row: CarbonSourceRow): CarbonDerivation {
  const cpuNameCandidate =
    str(row.cpuModel) ?? str(row.cpuFamilyText) ?? str(row.modelName) ?? str(row.displayName);
  if (!cpuNameCandidate) {
    return {
      kind: 'unsupported',
      reason: 'cpu SKU with no resolvable CPU identity (no cpu_model / cpu_family_text / model_name)',
    };
  }
  const defaulted: Record<string, string> = {};
  const cpu: CpuComponentPayload = { units: 1 };
  if (row.cpuCores != null) cpu.core_units = row.cpuCores;
  else defaulted['cpu.core_units'] = 'archetype_default (no cpu_cores on active OEM spec)';

  return { kind: 'cpu_component', payload: { cpu }, cpuNameCandidate, defaultedFields: defaulted };
}

/**
 * Derive a standalone memory SKU's BoaviztAPI component input. UNLIKE
 * deriveRamConfiguration (which estimates an INSTALLED fraction of a
 * server's max-SUPPORTED RAM ceiling — max_ram_gb is never an installed
 * figure there), a standalone memory SKU IS the module: its
 * `class_attributes.capacity_gb` is the module's own exact,
 * manufacturer-published capacity (e.g. "64GB DDR5 6400MHz ECC RDIMM Server
 * Memory" → capacity_gb=64) — there is no ceiling to fraction down and no
 * RAM_FILL_RATIO applies, same reasoning as the soldered-memory path
 * (SIL-413): no partially-populated configuration exists to model for a
 * single module. `units: 1` — the SKU is one module. Checks both the
 * `capacity_gb` key actually seeded on today's 2 memory SKUs and the
 * `memory_capacity_gb` key the asset_class spec_display_schema documents
 * (542_network_memory_spec_display_profiles.sql) in case a future scrape
 * writes that key instead — AND both keys' camelCase form, since Kysely's
 * shared CamelCasePlugin rewrites keys INSIDE jsonb result values too (same
 * gotcha select-spec-row.ts's pickRawString documents for raw_payload:
 * 'capacity_gb' arrives as 'capacityGb'). Verified against production data:
 * both of today's 2 memory SKUs carry `capacityGb`, never `capacity_gb`.
 *
 * @pure
 */
function deriveMemoryComponentInput(row: CarbonSourceRow): CarbonDerivation {
  const attrs = normalizeClassAttributes(row.classAttributes);
  // camelCase keys (capacityGb) are folded to snake_case by normalizeClassAttributes.
  const capacityGb = num(attrs['capacity_gb']) ?? num(attrs['memory_capacity_gb']);
  if (capacityGb == null) {
    return {
      kind: 'unsupported',
      reason: 'memory SKU with no resolvable capacity (no capacity_gb / memory_capacity_gb on class_attributes)',
    };
  }
  return {
    kind: 'memory_component',
    payload: { ram: { units: 1, capacity: Math.round(capacityGb) } },
    defaultedFields: {},
  };
}

// ── SIL-638: rack_system (multi-node) SKUs ───────────────────────────────────

/**
 * Derive a rack-scale SKU's BoaviztAPI input — see the module-header
 * "rack_system composition" note for the shape and its known limitations.
 * Pure. Returns `unsupported` (never a partial/guessed composition) when any
 * required class_attributes field is missing.
 *
 * @pure
 */
function deriveRackSystemConfig(row: CarbonSourceRow): CarbonDerivation {
  const attrs = normalizeClassAttributes(row.classAttributes);

  const nodeCount = num(attrs['node_count']);
  const gpusPerNode = num(attrs['gpus_per_node']);
  const totalGpuCountAttr = num(attrs['total_gpu_count']) ?? num(attrs['gpu_count_total']);
  const cpuCountPerNode = num(attrs['cpu_count_per_node']);
  const ramPerNodeGb = num(attrs['ram_per_node_gb']);
  const cpuNameCandidate = str(attrs['cpu_model']) ?? str(row.cpuModel) ?? str(row.cpuFamilyText);
  const gpuName = resolveGpuName(row);

  const totalGpuUnits =
    totalGpuCountAttr ?? (nodeCount != null && gpusPerNode != null ? nodeCount * gpusPerNode : null);

  const missing: string[] = [];
  if (nodeCount == null || nodeCount <= 0) missing.push('node_count');
  if (totalGpuUnits == null || totalGpuUnits <= 0) missing.push('gpus_per_node (or total_gpu_count)');
  if (!gpuName) missing.push('gpu_model');
  if (cpuCountPerNode == null || cpuCountPerNode <= 0) missing.push('cpu_count_per_node');
  if (!cpuNameCandidate) missing.push('cpu_model');
  if (ramPerNodeGb == null || ramPerNodeGb <= 0) missing.push('ram_per_node_gb');

  if (missing.length > 0) {
    return {
      kind: 'unsupported',
      reason: `rack_system SKU missing required class_attributes field(s): ${[...new Set(missing)].join(', ')}`,
    };
  }

  const defaulted: Record<string, string> = {
    'rack.scope':
      'BoaviztAPI composition covers compute-tray server chassis + GPU components only — NVSwitch/NVLink ' +
      'switch trays, the rack frame, liquid-cooling manifolds/CDUs, and cabling are NOT modeled. This figure ' +
      "is a KNOWN UNDERCOUNT relative to a manufacturer's whole-rack PCF, not a complete equivalent (SIL-638).",
    'ram':
      `per-node RAM taken exactly from class_attributes.ram_per_node_gb=${ramPerNodeGb} (no RAM_FILL_RATIO ` +
      'estimate — this is meant to be the compute tray\'s actual shipped configuration, not a max-supported ' +
      'ceiling to fraction down), reported as a single unit=1 block per tray.',
  };

  const perNodeServer: ServerConfigurationPayload = {
    model: { type: 'rack' },
    configuration: {
      cpu: { units: cpuCountPerNode! },
      ram: [{ units: 1, capacity: integerModuleCapacityGb(ramPerNodeGb!, 1) }],
    },
  };

  return {
    kind: 'rack_system',
    payload: {
      server: perNodeServer,
      nodeCount: nodeCount!,
      gpu: { name: gpuName!, units: totalGpuUnits! },
    },
    cpuNameCandidate,
    defaultedFields: defaulted,
  };
}

/**
 * Derive the BoaviztAPI input payload for one SKU or one ASSET. Pure — the
 * caller is responsible for calling BoaviztAPI and persisting the snapshot.
 *
 * `installedRamGb` (SIL-396) is the ASSET's own class_attributes.
 * installed_ram_gb, when computing at asset grain; omitted (or null) at SKU
 * grain, where the RAM ladder can only ever reach rung 2 (estimated) / rung
 * 3 (archetype) — see deriveServerConfig.
 */
export function deriveCarbonInput(row: CarbonSourceRow, installedRamGb: number | null = null): CarbonDerivation {
  if (row.assetClassId === 'gpu') {
    // Prefer the explicit gpu_model attribute / OEM spec field; otherwise
    // send the SKU's own model name — BoaviztAPI fuzzy-matches what it can
    // and falls back to its DEFAULT archetype for the rest (the CLI records
    // that fallback in defaulted_fields via name_to_gpu).
    const attrs = normalizeClassAttributes(row.classAttributes);
    const name = str(attrs['gpu_model']) ?? str(row.gpuModel) ?? str(row.modelName);
    if (!name) {
      return { kind: 'unsupported', reason: 'gpu SKU with no resolvable GPU model name' };
    }
    return { kind: 'gpu', payload: { gpu: { name, units: 1 } }, defaultedFields: {} };
  }

  if (row.assetClassId === 'server') {
    const { server, cpuNameCandidate, defaulted, ramProvenanceKind } = deriveServerConfig(row, installedRamGb);
    const attrs = normalizeClassAttributes(row.classAttributes);
    const gpuSlots = num(attrs['gpu_slots']);
    const knownUnits = gpuSlots ?? row.maxGpuCount;
    let gpuName = resolveGpuName(row);
    const platform = resolveGpuPlatform(row, gpuName);
    if (!gpuName && platform) {
      // SIL-2178: a fixed-baseboard GPU platform (XE9680) with no GPU model
      // anywhere on the row. Use the platform's documented default model
      // rather than pricing the bare chassis, and disclose it.
      gpuName = platform.defaultGpuName;
      defaulted['gpu.model'] =
        `${platform.defaultGpuName} assumed (${platform.platform} is a fixed-baseboard GPU platform; no GPU model on the SKU row or in its name)`;
    }

    if (gpuName) {
      // SIL-388: capacity, not populated count. gpu_slots (class attribute)
      // is preferred over max_gpu_count (OEM spec). SIL-2178: when neither
      // column is present, an explicit count in the SKU's own name, then the
      // platform table, before the last-resort assumed 1, so a name or
      // platform that clearly states 8 GPUs never becomes 1.
      let gpuUnits: number;
      let unitsSource: AcceleratorUnitsSource;
      const namedCount = resolveNamedGpuCount(row);
      if (gpuSlots != null && gpuSlots > 0) {
        gpuUnits = gpuSlots;
        unitsSource = 'gpu_slots';
      } else if (row.maxGpuCount != null && row.maxGpuCount > 0) {
        gpuUnits = row.maxGpuCount;
        unitsSource = 'max_gpu_count';
      } else if (namedCount != null) {
        gpuUnits = namedCount;
        unitsSource = 'name_count';
        defaulted['gpu.units'] = `${namedCount} (explicit count in the SKU name; no gpu_slots / max_gpu_count)`;
      } else if (platform) {
        gpuUnits = platform.gpuUnits;
        unitsSource = 'platform_table';
        defaulted['gpu.units'] =
          `${platform.gpuUnits} (${platform.platform}, GPU platform table: ${platform.source}; no gpu_slots / max_gpu_count / count in name)`;
      } else {
        gpuUnits = 1;
        unitsSource = 'assumed_one';
        defaulted['gpu.units'] =
          'assumed 1 (GPU model resolvable but no gpu_slots / max_gpu_count / count in name / platform table entry)';
      }

      const tdpMatch = resolveAcceleratorTdp(gpuName);
      if (!tdpMatch) {
        defaulted['gpu.tdp'] =
          `no known TDP for "${gpuName}" (SIL-389 table has no entry for this model) — accelerator draw omitted from the archetype-default power figure`;
      }
      const accelerator: AcceleratorPowerDerivation = {
        unitsSource,
        tdp: tdpMatch
          ? {
              name: tdpMatch.name,
              tdpWatts: tdpMatch.tdpWatts,
              derateFactor: USE_PHASE_GPU_WORKLOAD_DERATE_FACTOR,
              avgPowerWatts: tdpMatch.tdpWatts * USE_PHASE_GPU_WORKLOAD_DERATE_FACTOR,
            }
          : null,
      };

      return {
        kind: 'gpu_server',
        payload: { server, gpu: { name: gpuName, units: gpuUnits } },
        cpuNameCandidate,
        defaultedFields: defaulted,
        accelerator,
        ramProvenanceKind,
      };
    }
    if ((knownUnits ?? 0) > 0 && !gpuName) {
      defaulted['gpu'] = `omitted (${knownUnits} gpu slot(s) but no resolvable GPU model name)`;
    }
    return { kind: 'server', payload: { server }, cpuNameCandidate, defaultedFields: defaulted, ramProvenanceKind };
  }

  // SIL-433: component-grain standalone cpu/memory SKUs — see the module
  // section above for why these reuse the server path's guarded CPU
  // resolution / RAM reasoning rather than inventing a third scheme.
  if (row.assetClassId === 'cpu') return deriveCpuComponentInput(row);
  if (row.assetClassId === 'memory') return deriveMemoryComponentInput(row);
  // SIL-638: rack-scale multi-node SKUs — see deriveRackSystemConfig.
  if (row.assetClassId === 'rack_system') return deriveRackSystemConfig(row);

  return {
    kind: 'unsupported',
    reason: `asset_class '${row.assetClassId}' has no BoaviztAPI methodology (server/gpu/cpu/memory/rack_system only)`,
  };
}
